import logo from "../../assets/Short_logo_dark@4x.png";
import { useEffect, useMemo, useState, useRef } from "react";
import axios from "../../api/axios";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useInput from "../../hooks/useInput";
import useToggle from "../../hooks/useToggle";
import { LoadingOverlay } from "@mantine/core";

const LOGIN_URL = "/websiteAuth";

const styles = {
  wrapper: {
    display: "flex",
    minHeight: "100vh",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    backgroundColor: "#ffffff",
  },
  card: {
    width: "100%",
    maxWidth: "400px",
    padding: "24px",
    borderRadius: "8px",
    boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
    position: "relative",
    backgroundColor: "#fff",
  },
  logo: {
    display: "block",
    margin: "0 auto",
    height: "48px",
    width: "auto",
  },
  heading: {
    textAlign: "center",
    marginTop: "24px",
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1F2937",
  },
  form: {
    marginTop: "24px",
  },
  input: {
    display: "block",
    width: "100%",
    borderRadius: "4px",
    border: "1px solid #E5E7EB",
    padding: "12px",
    fontSize: "16px",
    lineHeight: "24px",
    marginBottom: "16px",
  },
  button: {
    width: "100%",
    padding: "12px",
    fontSize: "16px",
    fontWeight: "bold",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "#E4C577",
    color: "#FFFFFF",
  },
  error: {
    marginTop: "16px",
    color: "#e53935",
    fontWeight: "500",
    textAlign: "center",
  },
};

export default function LoginForm() {
  const { setAuth, username } = useAuth();
  const { t } = useTranslation();
  const resiWeb = username;

  const isSubTenant = useMemo(() => {
    const host = String(window?.location?.hostname || "").trim().toLowerCase();
    const effective = (host && host !== "localhost" && host !== "127.0.0.1")
      ? host
      : String(resiWeb || "").trim().toLowerCase();

    const parts = effective.split(".").filter(Boolean);
    return parts.length > 2;
  }, [resiWeb]);

  const navigate = useNavigate();
  // const location = useLocation();
  // const from = location.state?.from?.pathname || "/";

  const userRef = useRef();
  // const errRef = useRef();

  // const [user, setUser] = useState('');
  const [user, resetUser, userAttribs] = useInput("user", "");
  const [pwd, setPwd] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [check] = useToggle("persist", true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    userRef.current.focus();
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [user, pwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        LOGIN_URL,
        JSON.stringify({ user, pwd, resiWeb }),
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      const accessToken = response?.data?.accessToken;
      const role = response?.data?.role;
      const newUserName = response?.data?.username;
      setAuth({ username, user: newUserName, pwd, role, resiWeb, accessToken });
      // setUser('');
      resetUser();
      setPwd("");
      navigate("/edit/dashboard", { replace: true }); //TODO: navigate where??
      // navigate(from, {replace: true});
    } catch (err) {
      if (!err?.response?.data) {
        setErrMsg("The server is not responding, please try again later.");
      } else if (err.response?.data?.message) {
        setErrMsg(err.response.data.message);
      } else {
        setErrMsg("Login failed. Unknown error.");
      }
      // errRef.current.focus();
      // console.error(err);
    }
    finally {
      setLoading(false);
    }
  };

  // const togglePersist = () => {
  //   setPersist(prev => !prev);
  // }

  // useEffect(() => {
  //   localStorage.setItem("persist", persist);
  // }, [persist])

  return (
    <>
      {/*
        This example requires updating your template:

        ```
        <html className="h-full bg-gray-50">
        <body className="h-full">
        ```
      */}

      {
        <div style={styles.wrapper}>
          <div style={styles.card}>
            <LoadingOverlay visible={loading} overlayBlur={2} />
            <img style={styles.logo} src={logo} alt="ResiRent" />
            <h2 style={styles.heading}>{t("login.title")}</h2>

            <form onSubmit={handleSubmit} style={styles.form}>
              <input
                id="email-address"
                name="email"
                type="email"
                ref={userRef}
                autoComplete="email"
                required
                style={styles.input}
                placeholder={t("login.labelmail")}
                {...userAttribs}
              />
              <div style={{ position: "relative", marginBottom: "16px" }}>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  placeholder={t("login.labelpass")}
                  style={{
                    width: "100%",
                    borderRadius: "4px",
                    border: "1px solid #E5E7EB",
                    padding: "12px 44px 12px 12px",
                    fontSize: "16px",
                    lineHeight: "24px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  onFocus={(e) => (e.target.style.borderColor = "#E4C577")}
                  onBlur={(e) => (e.target.style.borderColor = "#E5E7EB")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#9CA3AF",
                  }}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ display: "block" }}
                    >
                      <path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-7 0-11-8-11-8a19.74 19.74 0 0 1 5.11-5.11" />
                      <path d="M1 1l22 22" />
                      <path d="M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-.88" />
                      <path d="M14.12 9.88A3 3 0 0 1 12 9a3 3 0 0 1-2.12.88" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ display: "block" }}
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>

              {isSubTenant && (
                <div style={{ marginTop: "-6px", marginBottom: "16px", textAlign: "right" }}>
                  <a
                    href={`https://resirent.org/forgot-password-domain?domain=${encodeURIComponent(String(resiWeb || "").trim().toLowerCase())}`}
                    style={{
                      color: "#1F2937",
                      fontSize: "14px",
                      textDecoration: "underline",
                    }}
                  >
                    Reset password
                  </a>
                </div>
              )}

              <button
                type="submit"
                style={{
                  ...styles.button,
                  cursor: check ? "pointer" : "default",
                }}
              >
                {t("login.login")}
              </button>
            </form>

            {errMsg && <div style={styles.error}>{errMsg}</div>}
          </div>
        </div>
      }
    </>
  );
}

// import logo from '../../assets/Short_logo_dark@4x.png'
// import { useEffect, useState, useRef } from 'react';
// import axios from '../../api/axios';
// import useAuth from '../../hooks/useAuth';
// import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router-dom';
// import useInput from '../../hooks/useInput';
// import useToggle from '../../hooks/useToggle';

// const LOGIN_URL = '/websiteAuth';

// export default function LoginForm() {

//     const { setAuth, username } = useAuth();
//     const { t } = useTranslation();
//     const resiWeb = username;

//     const navigate = useNavigate();
//     // const location = useLocation();
//     // const from = location.state?.from?.pathname || "/";

//     const userRef = useRef();
//     // const errRef = useRef();

//     // const [user, setUser] = useState('');
//     const [user, resetUser, userAttribs] = useInput('user', '');
//     const [pwd, setPwd] = useState('');
//     const [errMsg, setErrMsg] = useState('');
//     const [check] = useToggle('persist', true);

//     useEffect(() => {
//         userRef.current.focus();
//     }, [])

//     useEffect(() => {
//         setErrMsg('');
//     }, [user, pwd])

//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         try {
//             const response = await axios.post(LOGIN_URL,
//                 JSON.stringify({ user, pwd, resiWeb }),
//                 {
//                     headers: { 'Content-Type': 'application/json' },
//                     withCredentials: true
//                 }
//             );
//             const accessToken = response?.data?.accessToken;
//             const role = response?.data?.role;
//             setAuth({ user, pwd, role, resiWeb, accessToken });
//             // setUser('');
//             resetUser();
//             setPwd('');
//             navigate('/edit/dashboard', { replace: true }); //TODO: navigate where??
//             // navigate(from, {replace: true});

//         } catch (err) {
//             if (!err?.response?.data) {
//                 setErrMsg("The server is not responding, please try again later.");
//             } else if (err.response?.data?.message) {
//                 setErrMsg(err.response.data.message)
//             } else {
//                 setErrMsg("Login failed. Unknown error.")
//             }
//             // errRef.current.focus();
//             // console.error(err);
//         }
//     }

//     // const togglePersist = () => {
//     //   setPersist(prev => !prev);
//     // }

//     // useEffect(() => {
//     //   localStorage.setItem("persist", persist);
//     // }, [persist])

//     return (
//         <>
//             {/*
//         This example requires updating your template:

//         ```
//         <html className="h-full bg-gray-50">
//         <body className="h-full">
//         ```
//       */}

//             {
//                 <div style={{ display: "flex", minHeight: "100vh", alignItems: "center", justifyContent: "center", padding: "24px 16px", background: "white" }}>
//                     <div style={{ boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)", borderRadius: "8px", width: "100%", maxWidth: "md", marginTop: "32px", padding: "24px" }}>
//                         <div>
//                             <img style={{ display: "block", marginLeft: "auto", marginRight: "auto", height: "48px", width: "auto" }} href="/" src={logo} alt="ResiRent" />
//                             <h2 style={{ textAlign: "center", marginTop: "24px", fontSize: "24px", fontWeight: "bold", color: "#1F2937" }}>{t("login.title")}</h2>
//                         </div>
//                         <form onSubmit={handleSubmit} style={{ marginTop: "24px" }} action="#" method="POST">
//                             <input type="hidden" name="remember" defaultValue="true" />
//                             <div style={{ marginTop: "16px" }}>
//                                 <label htmlFor="email-address" style={{ display: "none" }}>{t("login.labelmail")}</label>
//                                 <input
//                                     id="email-address"
//                                     name="email"
//                                     type="email"
//                                     ref={userRef}
//                                     autoComplete="email"
//                                     required
//                                     style={{ marginBottom: "16px", display: "block", width: "100%", borderRadius: "4px", border: "1px solid #E5E7EB", padding: "12px", fontSize: "16px", lineHeight: "24px" }}
//                                     placeholder={t("login.labelmail")}
//                                     {...userAttribs}
//                                 />
//                             </div>
//                             <div style={{ marginTop: "16px" }}>
//                                 <label htmlFor="password" style={{ display: "none" }}>{t("login.labelpass")}</label>
//                                 <input
//                                     id="password"
//                                     name="password"
//                                     type="password"
//                                     autoComplete="current-password"
//                                     required
//                                     style={{ marginBottom: "16px", display: "block", width: "100%", borderRadius: "4px", border: "1px solid #E5E7EB", padding: "12px", fontSize: "16px", lineHeight: "24px" }}
//                                     placeholder={t("login.labelpass")}
//                                     value={pwd}
//                                     onChange={(e) => setPwd(e.target.value)}
//                                 />
//                             </div>
//                             <div style={{ marginTop: "24px" }}>
//                                 <button
//                                     type="submit"
//                                     style={{
//                                         width: "100%",
//                                         padding: "12px",
//                                         fontSize: "16px",
//                                         fontWeight: "bold",
//                                         borderRadius: "4px",
//                                         border: "none",
//                                         backgroundColor: "#E4C577",
//                                         color: "#FFFFFF",
//                                         cursor: check ? "pointer" : "default",
//                                     }}
//                                 >
//                                     {t("login.login")}
//                                 </button>
//                             </div>
//                         </form>
//                         {errMsg && <div className='hint-msg'>{errMsg}</div>}
//                     </div>
//                 </div>
//             }
//         </>
//     )
// }
